import { memo, useState } from "react";
import { useTranslations } from "next-intl";
import { useNavigate as useRouter } from "react-router-dom";
import { IconLogout } from "@tabler/icons-react";
import { IconButton } from "../Buttons/IconButton";
import { useUserSidebar } from "./useUserSidebar";
import { useAuth } from "@/lib/auth/AuthContext";
import { isPathActive, isSamePath } from "@/lib/routing/activePath";
import { LegalLinks } from "@/components/Legal/LegalLinks";

type UserSidebarProps = {
  role: string | null;
  customerType: string;
};

type UserMenuItem = {
  name: string;
  icon: React.ElementType;
  url: string;
};

type UserMenuRowProps = {
  item: UserMenuItem;
  isActive: boolean;
  /** The current page IS this entry — only then is the link disabled. */
  isExact: boolean;
  navigateTo: (url: string) => void;
  prefetch: (url: string) => void;
};

const UserMenuRow = memo(function UserMenuRow({
  item,
  isActive,
  isExact,
  navigateTo,
  prefetch,
}: UserMenuRowProps) {
  const href = `/user/${item.url}`;
  return (
    <div>
      <IconButton
        href={href}
        onClickAction={() => navigateTo(href)}
        onHoverAction={() => prefetch(href)}
        active={isActive}
        disableWithoutShowing={isExact}
        icon={item.icon}
        text={item.name}
        textClassName="hidden md:block"
        width="w-auto md:w-full"
        padding="p-2.5 md:p-3"
        space="space-x-0 md:space-x-3"
      />
    </div>
  );
});

export const UserSidebar: React.FC<UserSidebarProps> = ({ role, customerType }) => {
  const t = useTranslations("userSettings");
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { logout } = useAuth();

  const handleLogout = async () => {
    setLoading(true);
    await logout();
    router("/auth/login");
  };

  const { menuItems, navigateTo, prefetch, currentUrl } = useUserSidebar(
    t,
    role,
    customerType,
  );

  return (
    // min-w-0/max-w-full + overflow-x-auto: below md this is a horizontal
    // icon strip — with 9+ sections it exceeds 375px and (html clips x-overflow)
    // the trailing tabs were UNREACHABLE on phones. Now the strip scrolls.
    <div className="flex flex-row md:flex-col md:h-full max-w-full min-w-0 pr-0 pb-1 md:pb-0 md:pr-3 gap-1">
      <nav className="flex flex-row md:flex-col items-center md:items-stretch flex-1 min-w-0 overflow-x-auto no-scrollbar md:overflow-y-auto gap-1">
        {menuItems.map((item) => {
          const base = `/user/${item.url}`;
          return (
          <UserMenuRow
            key={item.url}
            item={item}
            isActive={isPathActive(currentUrl, base)}
            // Lit on its sub-pages too (`collaborators/invite`), but only the
            // exact page disables the link — from a sub-page it leads back.
            isExact={isSamePath(currentUrl, base)}
            navigateTo={navigateTo}
            prefetch={prefetch}
          />
          );
        })}
        <IconButton
          loading={loading}
          color="font-error"
          icon={IconLogout}
          text={t("logout")}
          textClassName="hidden md:block"
          width="w-auto md:w-full"
          padding="p-2.5 md:p-3"
          space="space-x-0 md:space-x-3"
          onClickAction={handleLogout}
        />
        <LegalLinks
          inline
          iconOnlyBelowMd
          className="flex flex-row md:flex-wrap items-center gap-3 md:gap-x-1.5 md:gap-y-0 px-2.5 md:px-3 md:pt-2"
        />
      </nav>
    </div>
  );
};
